import { useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Stack,
  Dropdown,
  IDropdownOption,
  IColumn,
  IconButton,
  MessageBar,
  MessageBarType,
  Toggle,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import { useApi } from '../hooks/useApi'
import { DataTable } from '../components/DataTable'
import { StatusBadge, CredentialTypeBadge } from '../components/StatusBadge'
import type { Credential, PagedResponse } from '../types'

export function Credentials() {
  const navigate = useNavigate()
  // Allow deep-links from the Dashboard, e.g. /credentials?expiring=30 or ?includeExpired=true
  const [searchParams] = useSearchParams()
  const [credentialType, setCredentialType] = useState<string>(searchParams.get('type') || '')
  const [expiringFilter, setExpiringFilter] = useState<number | undefined>(
    searchParams.get('expiring') ? Number(searchParams.get('expiring')) : undefined,
  )
  const [includeExpired, setIncludeExpired] = useState(searchParams.get('includeExpired') === 'true')
  const [page, setPage] = useState(1)
  const pageSize = 25

  const fetchCredentials = useCallback(
    () => apiService.getCredentials({
      page,
      pageSize,
      credentialType: credentialType || undefined,
      expiringWithinDays: expiringFilter,
      includeExpired,
    }),
    [page, pageSize, credentialType, expiringFilter, includeExpired]
  )

  const {
    data: credentials,
    loading,
    error,
  } = useApi<PagedResponse<Credential>>(fetchCredentials, [page, credentialType, expiringFilter, includeExpired])

  const typeOptions: IDropdownOption[] = [
    { key: '', text: 'All Types' },
    { key: 'Certificate', text: 'Certificate' },
    { key: 'ClientSecret', text: 'Client Secret' },
    { key: 'FederatedCredential', text: 'Federated Credential' },
  ]

  const expirationOptions: IDropdownOption[] = [
    { key: '', text: 'All Credentials' },
    { key: '7', text: 'Expiring in 7 days' },
    { key: '30', text: 'Expiring in 30 days' },
    { key: '60', text: 'Expiring in 60 days' },
    { key: '90', text: 'Expiring in 90 days' },
  ]

  const columns: IColumn[] = [
    {
      key: 'application',
      name: 'Application',
      minWidth: 280,
      onRender: (item: Credential) => (
        <Stack title={`${item.applicationName}\n${item.entraAppId}`}>
          <span style={{ fontWeight: 500 }}>{item.applicationName}</span>
          <span style={{ fontSize: 11, color: '#605e5c', fontFamily: 'monospace' }}>
            {item.entraAppId}
          </span>
        </Stack>
      ),
    },
    {
      key: 'credential',
      name: 'Credential',
      minWidth: 150,
      onRender: (item: Credential) => (
        <Stack title={`${item.displayName || 'Unnamed'}${item.hint ? `\nHint: ${item.hint}` : ''}`}>
          <span>{item.displayName || 'Unnamed'}</span>
          {item.hint && (
            <span style={{ fontSize: 11, color: '#605e5c' }}>Hint: {item.hint}</span>
          )}
        </Stack>
      ),
    },
    {
      key: 'type',
      name: 'Type',
      minWidth: 100,
      onRender: (item: Credential) => <CredentialTypeBadge type={item.credentialType} />,
    },
    {
      key: 'endDate',
      name: 'Expires',
      minWidth: 100,
      onRender: (item: Credential) => {
        const dateStr = new Date(item.endDate).toLocaleDateString()
        return <span title={dateStr}>{dateStr}</span>
      },
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 90,
      onRender: (item: Credential) => (
        <StatusBadge expiresInDays={item.expiresInDays} isExpired={item.isExpired} />
      ),
    },
    {
      key: 'owner',
      name: 'Owner',
      minWidth: 180,
      onRender: (item: Credential) => {
        const owner = item.primaryOwnerEmail || 'Unassigned'
        return <span title={owner}>{owner}</span>
      },
    },
    {
      key: 'notifications',
      name: 'Notifications',
      minWidth: 100,
      onRender: (item: Credential) => {
        const text = item.suppressNotifications ? 'Suppressed' : `${item.notificationCount} sent`
        return (
          <span title={text} style={{ color: item.suppressNotifications ? '#a4262c' : '#107c10' }}>
            {text}
          </span>
        )
      },
    },
    {
      key: 'actions',
      name: '',
      minWidth: 50,
      maxWidth: 50,
      isResizable: false,
      onRender: (item: Credential) => (
        <IconButton
          iconProps={{ iconName: 'ChevronRight' }}
          title="View Details"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/credentials/${item.credentialId}`)
          }}
        />
      ),
    },
  ]

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Credentials</h1>
        <p className="page-description">
          View and manage all application credentials across your organization
        </p>
      </div>

      {/* Filters */}
      <div className="card">
        <Stack horizontal tokens={{ childrenGap: 16 }} wrap verticalAlign="end">
          <Dropdown
            label="Credential Type"
            options={typeOptions}
            selectedKey={credentialType}
            onChange={(_, option) => {
              setCredentialType(option?.key?.toString() || '')
              setPage(1)
            }}
            styles={{ root: { width: 180 } }}
          />
          <Dropdown
            label="Expiration"
            options={expirationOptions}
            selectedKey={expiringFilter?.toString() || ''}
            onChange={(_, option) => {
              setExpiringFilter(option?.key ? Number(option.key) : undefined)
              setPage(1)
            }}
            styles={{ root: { width: 180 } }}
          />
          <Toggle
            label="Include Expired"
            checked={includeExpired}
            onChange={(_, checked) => {
              setIncludeExpired(!!checked)
              setPage(1)
            }}
          />
        </Stack>
      </div>

      {/* Results */}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          styles={{ root: { marginBottom: 16 } }}
        >
          {error}
        </MessageBar>
      )}

      <div className="card">
        <DataTable
          items={credentials?.items || []}
          columns={columns}
          loading={loading}
          emptyMessage="No credentials found"
          onItemClick={(item) => navigate(`/credentials/${item.credentialId}`)}
        />

        {/* Pagination */}
        {credentials && credentials.totalPages > 1 && (
          <Stack
            horizontal
            horizontalAlign="center"
            tokens={{ childrenGap: 8 }}
            styles={{ root: { marginTop: 16 } }}
          >
            <IconButton
              iconProps={{ iconName: 'ChevronLeft' }}
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            />
            <span style={{ padding: '8px 16px' }}>
              Page {page} of {credentials.totalPages}
            </span>
            <IconButton
              iconProps={{ iconName: 'ChevronRight' }}
              disabled={page === credentials.totalPages}
              onClick={() => setPage(p => p + 1)}
            />
          </Stack>
        )}
      </div>
    </div>
  )
}
